import { Alert, Button, Field, Input } from '@personachat/ui';
import { useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { ErreurApi, lire, messageDe } from '../../api/call';
import { Modal } from '../../components/Modal';
import { useToast } from '../../components/Toast';
import { formatDate } from '../../lib/format';
import { LIBELLE_TYPE, type TypeCompte } from '../../lib/labels';

/**
 * Transformer un compte élève en compte administrateur ou super admin (super
 * admin seulement). Une adresse ne porte qu'un compte : quand l'adresse à
 * inviter est celle d'une élève, on transforme son compte. Deux entrées : la
 * fenêtre d'invitation (après le refus « compte élève ») et la fiche de l'élève.
 */

/** Élève qui porte déjà l'adresse invitée (réponse 409 STUDENT_ACCOUNT de l'invitation). */
export interface EleveExistant {
  id: string;
  name: string | null;
  userType: TypeCompte;
  createdAt: string;
  isActive: boolean;
  deleted: boolean;
}

export function eleveDeErreur(err: unknown): EleveExistant | null {
  if (!(err instanceof ErreurApi) || err.code !== 'STUDENT_ACCOUNT') return null;
  return (err.corps as { student?: EleveExistant } | undefined)?.student ?? null;
}

export function ChoixRole({ superAdmin, onChange }: { superAdmin: boolean; onChange: (v: boolean) => void }) {
  const nom = useId();
  const options: Array<{ valeur: boolean; titre: string; detail: string }> = [
    { valeur: false, titre: 'Admin', detail: 'Élèves, crédits, clés ; lecture des ventes, contenus et événements.' },
    { valeur: true, titre: 'Super admin', detail: 'Tout, y compris ventes, contenus IA, événements et administrateurs.' },
  ];
  return (
    <fieldset className="choix">
      <legend className="ui-field__label">Rôle</legend>
      {options.map((o) => (
        <label key={o.titre} className={superAdmin === o.valeur ? 'choix__option choix__option--active' : 'choix__option'}>
          <input type="radio" name={nom} checked={superAdmin === o.valeur} onChange={() => onChange(o.valeur)} />
          <span>
            <strong>{o.titre}</strong>
            <span className="muted small">{o.detail}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

/** Ce que change la transformation, dit avant de confirmer. */
export function ConsequencesPromotion() {
  return (
    <ul className="liste-aide note">
      <li>
        L’ancien mot de passe ne fonctionne plus et les sessions ouvertes se ferment. Une invitation part pour choisir un nouveau mot de passe
        (12 caractères au moins), valable 7 jours.
      </li>
      <li>Les conversations, coachs et crédits sont conservés. L’application élève reste accessible avec le nouveau mot de passe.</li>
      <li>Le compte sort des statistiques élèves et n’est plus débité de crédits.</li>
      <li>L’opération est inscrite au journal d’audit, avec le motif.</li>
    </ul>
  );
}

/** Rôle, conséquences, motif et bouton : le formulaire commun aux deux entrées. */
export function FormulairePromotion({
  email,
  superAdminInitial = false,
  libelleAnnuler = 'Annuler',
  onAnnuler,
  onFait,
}: {
  email: string;
  superAdminInitial?: boolean;
  libelleAnnuler?: string;
  onAnnuler: () => void;
  onFait: () => void;
}) {
  const id = useId();
  const toast = useToast();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [superAdmin, setSuperAdmin] = useState(superAdminInitial);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = async (e: FormEvent) => {
    e.preventDefault();
    if (motif.trim().length < 3 || envoi) return;
    setEnvoi(true);
    setErreur(null);
    try {
      const r = await lire(api.POST('/api/admin/admins/promote', { body: { email, superAdmin, motif: motif.trim() } }));
      if (r.emailSent) toast.succes(r.message);
      else toast.erreur(r.message);
      await Promise.all([['admins'], ['securite'], ['eleves'], ['eleve']].map((queryKey) => client.invalidateQueries({ queryKey })));
      onFait();
      navigate(`/administrateurs/${r.admin.id}`);
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <form className="stack" onSubmit={valider} noValidate>
      <ChoixRole superAdmin={superAdmin} onChange={setSuperAdmin} />
      <ConsequencesPromotion />
      <Field id={`${id}-motif`} label="Motif" hint="Obligatoire, inscrit au journal d’audit.">
        <Input
          id={`${id}-motif`}
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
          maxLength={300}
          placeholder="Ex. : rejoint l’équipe pédagogique en octobre"
        />
      </Field>
      {erreur && <Alert>{erreur}</Alert>}
      <div className="modal__footer modal__footer--inline">
        <Button onClick={onAnnuler} disabled={envoi}>
          {libelleAnnuler}
        </Button>
        <Button type="submit" variant="primary" icon={<ShieldCheck size={15} aria-hidden="true" />} loading={envoi} disabled={motif.trim().length < 3}>
          Transformer en {superAdmin ? 'super admin' : 'admin'}
        </Button>
      </div>
    </form>
  );
}

/** Dans la fenêtre d'invitation : l'adresse est celle d'une élève, on propose de transformer son compte. */
export function PromotionDepuisInvitation({
  email,
  eleve,
  superAdmin,
  onRetour,
  onFait,
}: {
  email: string;
  eleve: EleveExistant;
  superAdmin: boolean;
  onRetour: () => void;
  onFait: () => void;
}) {
  const navigate = useNavigate();
  const qui = (
    <>
      <strong>{eleve.name ?? email}</strong> ({LIBELLE_TYPE[eleve.userType]}, inscription le {formatDate(eleve.createdAt)})
    </>
  );
  const bloque = eleve.deleted ? 'supprimé : restaurez-le' : !eleve.isActive ? 'désactivé : réactivez-le' : null;

  if (bloque) {
    return (
      <div className="stack">
        <Alert>
          Cette adresse est celle d’un compte élève : {qui}. Ce compte est {bloque} d’abord depuis sa fiche, puis transformez-le en compte
          administrateur.
        </Alert>
        <div className="modal__footer modal__footer--inline">
          <Button onClick={onRetour}>Retour</Button>
          <Button
            variant="primary"
            onClick={() => {
              onFait();
              navigate(`/eleves/${eleve.id}`);
            }}
          >
            Ouvrir sa fiche
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <Alert tone="info">
        Cette adresse est celle d’un compte élève : {qui}. Une adresse ne porte qu’un compte : vous pouvez transformer celui-ci en compte
        administrateur.
      </Alert>
      <FormulairePromotion email={email} superAdminInitial={superAdmin} libelleAnnuler="Retour" onAnnuler={onRetour} onFait={onFait} />
    </div>
  );
}

/** Depuis la fiche d'une élève : « Passer en administrateur ». */
export function PromotionDialog({
  open,
  onOpenChange,
  eleve,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  eleve: { email: string; nom: string };
}) {
  const [cle, setCle] = useState(0);
  useEffect(() => {
    if (open) setCle((c) => c + 1);
  }, [open]);
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Passer en administrateur" description={`${eleve.nom} · ${eleve.email}`}>
      <FormulairePromotion key={cle} email={eleve.email} onAnnuler={() => onOpenChange(false)} onFait={() => onOpenChange(false)} />
    </Modal>
  );
}
